import React from 'react';
import type { FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';
import { styled } from '@compiled/react';

import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import CrossCircleIcon from '@atlaskit/icon/glyph/cross-circle';
import { token } from '@atlaskit/tokens';

const i18n = defineMessages({
	featureMessageOne: {
		id: 'external-collab-ui.guest-features-list.1',
		defaultMessage: 'Use Confluence without a paid license (limits apply)',
		description: 'First point describing what guests can do',
	},
	featureMessageTwo: {
		id: 'external-collab-ui.guest-features-list.2',
		defaultMessage: 'View, create, and edit in a single space',
		description: 'Second point describing what guests can do',
	},
	featureMessageThree: {
		id: 'external-collab-ui.guest-features-list.3',
		defaultMessage: 'Create spaces',
		description: 'Third point describing what guests can do',
	},
	featureMessageFour: {
		id: 'external-collab-ui.guest-features-list.4',
		// TODO: replace straight quotes with curly quotes (see go/curlyquotes)
		// eslint-disable-next-line no-restricted-syntax
		defaultMessage: "View other people's profiles",
		description: 'Fourth point describing what guests can do',
	},
	featureMessageFive: {
		id: 'external-collab-ui.guest-features-list.5',
		defaultMessage: 'Mention anyone',
		description: 'Fifth point describing what guests can do',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const InfoContainer = styled.div({
	display: 'flex',
	justifyContent: 'center',
	marginTop: token('space.200', '16px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ListContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'baseline',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const List = styled.ul({
	listStyle: 'none',
	marginBottom: token('space.150', '12px'),
	padding: token('space.0', '0px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ListItem = styled.li({
	display: 'flex',
	marginTop: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconWrapper = styled.div({
	flex: `0 0 ${token('space.200', '16px')}`,
	marginRight: token('space.100', '8px'),
	width: token('space.200', '16px'),
});

export type ExternalCollabFeatureMessage = MessageDescriptor & {
	isFeatureAvailable?: boolean;
};

export type ExternalCollabFeaturesListProps = {
	header?: React.ReactNode;
	featureMessages?: ExternalCollabFeatureMessage[];
	footer?: React.ReactNode;
};

const availableFeatures = [i18n.featureMessageOne, i18n.featureMessageTwo].map((message) => ({
	...message,
	isFeatureAvailable: true,
}));

const restrictedFeatures = [
	i18n.featureMessageThree,
	i18n.featureMessageFour,
	i18n.featureMessageFive,
];

const defaultFeatureMessages: ExternalCollabFeatureMessage[] = [
	...availableFeatures,
	...restrictedFeatures,
];

export const ExternalCollabFeaturesList: FC<ExternalCollabFeaturesListProps> = ({
	header,
	featureMessages = defaultFeatureMessages,
	footer,
}) => {
	return (
		<InfoContainer role="list" aria-label="external-collab-features-list">
			<ListContainer>
				{header}
				<List>
					{featureMessages.map(({ isFeatureAvailable, ...message }) => (
						<ListItem key={message.id}>
							<IconWrapper>
								{isFeatureAvailable ? (
									<CheckCircleIcon label="" primaryColor={token('color.icon.success', 'green')} />
								) : (
									<CrossCircleIcon label="" primaryColor={token('color.icon.danger', 'red')} />
								)}
							</IconWrapper>
							<FormattedMessage {...message} />
						</ListItem>
					))}
				</List>
				{footer}
			</ListContainer>
		</InfoContainer>
	);
};
